// Servicio para manejar el cambio de estado de lectura de chats (leído/no leído) en batch
import ChatActionsModelMC from '../../presentation/models/inbox/ChatActionsModelMC';
import { updateOrInsertConversationOptimized } from '../../actions/inbox/conversationOptimizedActions';
import { resolveInboxPlatform } from './dualCountersMergeService';
import { isInboxMsChat } from './inboxMsChatPlatform';
import { assertMsInboxUrlConfigured } from './msInboxClient';
import { sendChatReadStateMsInbox } from './msInboxReadChatClient';

function validateBodies(bodies, dispatch) {
  if (!Array.isArray(bodies) || bodies.length === 0) {
    dispatch({
      type: 'LOAD_DATA_FAILURE',
      error: 'No conversations provided to set chat read state'
    });
    return false;
  }
  return true;
}

async function updateLocalStates(dispatch, bodies, unreadState) {
  return Promise.all(bodies.map(async (body) => {
    const { id, platform } = body || {};
    if (!id || !platform) {
      dispatch({
        type: 'LOAD_DATA_FAILURE',
        error: `Missing data to set chat read state for id: ${id}`
      });
      return { id, success: false, error: 'Missing data to set chat read state' };
    }
    const changes = {
      has_unread_messages: unreadState,
      ...(unreadState ? {} : { unread_messages_count: 0 })
    };
    const updateResult = await dispatch(updateOrInsertConversationOptimized({
      id,
      changes
    }));
    if (!updateResult.success) {
      dispatch({
        type: 'LOAD_DATA_FAILURE',
        error: updateResult.error || `Error updating conversation read state for id: ${id}`
      });
      return { id, success: false, error: updateResult.error };
    }
    return { id, platform, success: true };
  }));
}

async function rollbackLocalStates(dispatch, bodies, unreadState) {
  return Promise.all(bodies.map(async (body) => {
    const { id } = body || {};
    const revertChanges = { has_unread_messages: !unreadState };
    await dispatch(updateOrInsertConversationOptimized({
      id,
      changes: revertChanges
    }));
  }));
}

async function sendLegacyBatchToServer(bodies, unreadState) {
  const method = unreadState ? 'unreadChat' : 'readChat';
  return ChatActionsModelMC[method]({ chats: bodies });
}

function getFailedChatsRollbackBodies(bodies, failedChats) {
  if (!Array.isArray(failedChats) || failedChats.length === 0) return [];
  return bodies.filter((b) => failedChats.includes(b.id));
}

function handleFailedChatsRollback(dispatch, bodies, failedChats, unreadState, localResults) {
  const rollbackBodies = getFailedChatsRollbackBodies(bodies, failedChats);
  return rollbackLocalStates(dispatch, rollbackBodies, unreadState)
    .then(() => {
      dispatch({
        type: 'LOAD_DATA_FAILURE',
        error: 'Algunos chats no pudieron actualizarse en el servidor',
        failedChats
      });
      return {
        success: false,
        error: 'Algunos chats no pudieron actualizarse en el servidor',
        failedChats,
        results: localResults
      };
    });
}

async function sendToServers(legacyBodies, msBodies, unreadState) {
  const hasLegacy = legacyBodies.length > 0;
  const hasMs = msBodies.length > 0;

  if (!hasLegacy && !hasMs) return { settled: null };

  if (hasMs) {
    assertMsInboxUrlConfigured();
  }

  if (hasLegacy && !hasMs) {
    const result = await sendLegacyBatchToServer(legacyBodies, unreadState);
    return { settled: null, legacyResult: result };
  }

  if (!hasLegacy && hasMs) {
    await sendChatReadStateMsInbox({ chats: msBodies }, unreadState);
    return { settled: null };
  }

  const settled = await Promise.allSettled([
    sendLegacyBatchToServer(legacyBodies, unreadState),
    sendChatReadStateMsInbox({ chats: msBodies }, unreadState)
  ]);
  return { settled };
}

/**
 * Cambia el estado de lectura de múltiples chats y maneja rollback en caso de error, concurrentemente.
 * @param {Function} dispatch - Función dispatch de Redux.
 * @param {Array<Object>} bodies - Array de objetos con datos para identificar cada conversación y cliente.
 * @param {Object} [options={}] - Opciones adicionales (ejemplo: unreadState).
 * @returns {Promise<Object>} Resultado de la operación con éxito o error por cada chat.
 */
export default async function handleChatReadState(dispatch, bodies, options = {}) {
  const validBodies = (bodies || []).filter((body) => resolveInboxPlatform(body?.platform));

  if (!validateBodies(validBodies, dispatch)) {
    return { success: false, error: 'No conversations provided to set chat read state' };
  }

  const { unreadState = false } = options;

  // 1. Actualizar estado local concurrentemente
  const localResults = await updateLocalStates(dispatch, validBodies, unreadState);
  if (localResults.some((r) => !r.success)) {
    return { success: false, results: localResults };
  }

  // 2. Particionar por destino: microservicio vs legado
  const msBodies = validBodies.filter((b) => isInboxMsChat(b?.platform));
  const legacyBodies = validBodies.filter((b) => !isInboxMsChat(b?.platform));

  if (msBodies.length === 0 && legacyBodies.length === 0) {
    return { success: true, results: localResults };
  }

  try {
    const { settled, legacyResult } = await sendToServers(legacyBodies, msBodies, unreadState);

    // Caso simple sin Promise.allSettled: verificar failed_chats del legado
    if (!settled) {
      if (legacyResult && Array.isArray(legacyResult.failed_chats) && legacyResult.failed_chats.length > 0) {
        return await handleFailedChatsRollback(dispatch, validBodies, legacyResult.failed_chats, unreadState, localResults);
      }
      return { success: true, results: localResults };
    }

    // Caso mixto con Promise.allSettled: rollback selectivo
    const legacySettled = settled[0];
    const msSettled = settled[1];
    const legacyFailed = legacySettled.status === 'rejected';
    const msFailed = msSettled.status === 'rejected';

    if (legacyFailed || msFailed) {
      const rollbackBodies = [
        ...(legacyFailed ? legacyBodies : []),
        ...(msFailed ? msBodies : [])
      ];
      await rollbackLocalStates(dispatch, rollbackBodies, unreadState);

      const msgParts = [];
      if (legacyFailed) msgParts.push(legacySettled.reason?.message || String(legacySettled.reason));
      if (msFailed) msgParts.push(msSettled.reason?.message || String(msSettled.reason));
      const errorMsg = msgParts.filter(Boolean).join(' | ') || 'Error setting chat read state (batch)';

      dispatch({ type: 'LOAD_DATA_FAILURE', error: errorMsg });
      return { success: false, error: errorMsg, results: localResults };
    }

    // Ambos exitosos: verificar failed_chats del legado
    if (legacySettled.value && Array.isArray(legacySettled.value.failed_chats) && legacySettled.value.failed_chats.length > 0) {
      return await handleFailedChatsRollback(dispatch, validBodies, legacySettled.value.failed_chats, unreadState, localResults);
    }

    return { success: true, results: localResults };
  } catch (error) {
    // Rollback de todos los bodies que iban a algún servidor
    const allServerBodies = [...legacyBodies, ...msBodies];
    await rollbackLocalStates(dispatch, allServerBodies, unreadState);
    dispatch({
      type: 'LOAD_DATA_FAILURE',
      error: error.message || 'Error setting chat read state (batch)'
    });
    return { success: false, error: error.message, results: localResults };
  }
}
